<script lang="ts">
  // « Sécurité » (docs/spec.md, Sessions): active sessions, password, recovery codes.
  import { onMount } from 'svelte';
  import { api, ApiError, type Security } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';

  let security = $state<Security | null>(null);
  let error = $state('');

  async function load() {
    try {
      security = await api.security();
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Impossible de charger la page';
    }
  }
  onMount(load);

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Une erreur est survenue');

  // --- Sessions ---------------------------------------------------------------------------
  let sessionError = $state('');
  async function revoke(id: string) {
    sessionError = '';
    try {
      await api.revokeSession(id);
      await load();
    } catch (e) {
      sessionError = message(e);
    }
  }

  // --- Password ---------------------------------------------------------------------------
  let current = $state('');
  let next = $state('');
  let confirmation = $state('');
  let passwordError = $state('');
  let passwordDone = $state(false);
  let saving = $state(false);
  async function changePassword(event: SubmitEvent) {
    event.preventDefault();
    passwordError = '';
    passwordDone = false;
    if (next !== confirmation) {
      passwordError = 'Les deux saisies du nouveau mot de passe diffèrent';
      return;
    }
    saving = true;
    try {
      await api.changePassword(current, next);
      current = next = confirmation = '';
      passwordDone = true;
      await load();
    } catch (e) {
      passwordError = message(e);
    } finally {
      saving = false;
    }
  }

  // --- Recovery codes ---------------------------------------------------------------------
  let codesPassword = $state('');
  let codes = $state<string[] | null>(null);
  let codesError = $state('');
  let kept = $state(false);
  async function regenerate(event: SubmitEvent) {
    event.preventDefault();
    codesError = '';
    try {
      codes = (await api.newRecoveryCodes(codesPassword)).recovery_codes;
      codesPassword = '';
      kept = false;
    } catch (e) {
      codesError = message(e);
    }
  }
  function download() {
    if (!codes) return;
    const text = `Codes de secours ${app.info?.app_name ?? 'Messagerie'} (${app.me?.email ?? app.me?.username})\n\n${codes.join('\n')}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'codes-de-secours.txt';
    a.click();
    URL.revokeObjectURL(url);
  }
  async function done() {
    codes = null;
    await load();
  }
</script>

<section class="page" aria-labelledby="security-title">
  <header>
    <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <div>
      <h1 id="security-title">Sécurité</h1>
      <p class="sub">{app.me?.display_name} · {app.me?.email ?? `@${app.me?.username}`}</p>
    </div>
  </header>

  <div class="column">
    {#if error}<p class="error" role="alert">{error}</p>{/if}

    <section class="card" aria-labelledby="sessions-title">
      <h2 id="sessions-title">Sessions actives</h2>
      <p class="hint">Les appareils connectés à votre compte. Déconnectez celui que vous ne reconnaissez pas, puis changez de mot de passe.</p>
      {#if sessionError}<p class="error" role="alert">{sessionError}</p>{/if}
      {#if !security}
        <p class="hint">Chargement…</p>
      {:else}
        <ul class="sessions">
          {#each security.sessions as s (s.id)}
            <li>
              <span class="device">
                <span class="label">{s.device_label || 'Appareil inconnu'}</span>
                <span class="meta">
                  {#if s.current}<span class="badge">Cet appareil</span>{/if}
                  Dernière activité : {dayAndTime(s.last_seen_at)} · ouverte : {dayAndTime(s.created_at)}
                </span>
              </span>
              {#if !s.current}
                <button type="button" class="btn" onclick={() => revoke(s.id)} aria-label="Déconnecter {s.device_label}">Déconnecter</button>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    {#if security && !security.has_password}
      <section class="card">
        <h2>Mot de passe et codes de secours</h2>
        <p class="hint">Ce compte n'a pas encore de mot de passe (mode développement) : il s'active avec un lien d'invitation.</p>
      </section>
    {:else if security}
      <section class="card" aria-labelledby="password-title">
        <h2 id="password-title">Mot de passe</h2>
        <p class="hint">Après le changement, vos autres appareils sont déconnectés.</p>
        <form onsubmit={changePassword}>
          <label class="field">Mot de passe actuel
            <input class="input" type="password" autocomplete="current-password" required bind:value={current} />
          </label>
          <label class="field">Nouveau mot de passe
            <input class="input" type="password" autocomplete="new-password" minlength="12" required bind:value={next} />
          </label>
          <label class="field">Nouveau mot de passe, encore une fois
            <input class="input" type="password" autocomplete="new-password" minlength="12" required bind:value={confirmation} />
          </label>
          <p class="hint">12 caractères au moins. Une phrase de quelques mots sans rapport entre eux convient bien.</p>
          {#if passwordError}<p class="error" role="alert">{passwordError}</p>{/if}
          {#if passwordDone}<p class="ok" role="status">Mot de passe changé. Les autres appareils ont été déconnectés.</p>{/if}
          <button type="submit" class="btn primary" disabled={saving}>Changer le mot de passe</button>
        </form>
      </section>

      <section class="card" aria-labelledby="codes-title">
        <h2 id="codes-title">Codes de secours</h2>
        {#if codes}
          <p class="hint">Voici vos nouveaux codes. Ils ne seront plus affichés : gardez-les en lieu sûr. Les anciens ne marchent plus.</p>
          <ol class="codes">
            {#each codes as code (code)}<li><code>{code}</code></li>{/each}
          </ol>
          <div class="row">
            <button type="button" class="btn" onclick={download}>Télécharger</button>
            <button type="button" class="btn" onclick={() => navigator.clipboard?.writeText(codes!.join('\n'))}>Copier</button>
          </div>
          <label class="check"><input type="checkbox" bind:checked={kept} /> J'ai mis ces codes en lieu sûr</label>
          <button type="button" class="btn primary" disabled={!kept} onclick={done}>Terminé</button>
        {:else}
          <p class="hint">
            Ils remplacent le code de l'application d'authentification si vous n'avez plus votre téléphone.
            Il vous en reste <strong>{security.recovery_codes_left}</strong> sur 10.
          </p>
          <form onsubmit={regenerate}>
            <label class="field">Mot de passe, pour confirmer
              <input class="input" type="password" autocomplete="current-password" required bind:value={codesPassword} />
            </label>
            {#if codesError}<p class="error" role="alert">{codesError}</p>{/if}
            <button type="submit" class="btn">Générer de nouveaux codes</button>
          </form>
        {/if}
      </section>
    {/if}
  </div>
</section>

<style>
  .page {
    height: 100%;
    overflow-y: auto;
    background: var(--bg-page);
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: var(--header-h);
    padding: 8px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  h1 {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
  }
  .sub {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .column {
    max-width: 720px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  h2 {
    margin: 0;
    font-size: 16px;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .sessions {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .sessions li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-top: 1px solid var(--border);
  }
  .device {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .label {
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .meta {
    font-size: 12px;
    color: var(--muted);
  }
  .badge {
    display: inline-block;
    margin-right: 6px;
    padding: 0 6px;
    border-radius: 8px;
    background: var(--status-done-soft);
    color: var(--status-done);
    font-weight: 600;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  form .field {
    width: 100%;
    max-width: 380px;
  }
  .codes {
    margin: 0;
    padding-left: 24px;
    columns: 2;
    font-family: var(--font-mono);
  }
  .codes code {
    font-size: 15px;
  }
  .row {
    display: flex;
    gap: 8px;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ok {
    margin: 0;
    color: var(--status-done);
    font-weight: 500;
  }
  .btn.primary {
    align-self: flex-start;
  }
  @media (max-width: 759px) {
    header {
      padding: 8px 12px;
    }
    .back {
      display: flex;
    }
    .column {
      padding: 12px;
    }
  }
</style>
